import { css } from '@emotion/react';
import styled from '@emotion/styled';
import * as Sentry from '@sentry/react';
import {
  FileUploadArea,
  ProgressSpinner,
  space,
} from 'folio-common-components';
import {
  folioEmailAddress,
  folioEmailAddressUrl,
  formatters,
  invariant,
} from 'folio-common-utils';
import { colors } from 'folio-design-tokens';
import * as React from 'react';
import type { MaBankSelection } from '../../../gqltypes';
import { setBankSelection } from '../../../services';
import { type RootState, useActions, useSelector } from '../../../state';
import { getOnboardingUrl } from '../../../utils';
import { track, trackLinkedinEvent } from '../../../utils/track';
import { Button } from '../../buttons';
import { PageHeading } from '../../page-heading';
import { StepContainer } from '../Container';

type Status = 'default' | 'uploading' | 'uploaded' | 'error' | 'wrong-file';

const UploadingSpinnerWrapper = styled.div`
  background: rgba(255, 255, 255, 0.5);
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  justify-content: center;
  align-items: center;
`;

function selector(state: RootState) {
  invariant(state.founding.foundingDetails);
  invariant(state.founding.uploadReceiptURL);
  return {
    id: state.founding.id,
    capital: state.founding.foundingDetails.capital,
    uploadReceiptURL: state.founding.uploadReceiptURL,
    documentUrl: state.founding.documentURL,
    companyName: state.founding.foundingDetails.name,
    bankSelection: state.founding.bankSelection,
  };
}

export const OtherBankUploadBankReceiptStep: React.FC = () => {
  const [status, setStatus] = React.useState<Status>('default');
  const [fileChosen, setFileChosen] = React.useState(false);
  const [fileChosenIsPdf, setFileChosenIsPdf] = React.useState(false);
  const {
    capital,
    companyName,
    id,
    documentUrl,
    uploadReceiptURL,
    bankSelection,
  } = useSelector(selector);
  const onboardingUrl = getOnboardingUrl(id, bankSelection);

  React.useEffect(
    () => {
      track('form', 'completed', `${companyName}`, 100, false);
      trackLinkedinEvent('2118740');
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const upload = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setStatus('uploading');

    const formEle = event.currentTarget;
    try {
      const req = await fetch(formEle.action, {
        method: formEle.method,
        body: new FormData(formEle),
      });
      const status = req.ok
        ? 'uploaded'
        : req.status === 418
        ? 'wrong-file'
        : 'error';
      setStatus(status);

      if (status === 'uploaded') {
        track(
          'form',
          'upload',
          `bankkontobekreftelse - ${companyName}`,
          200,
          false,
        );
      }
    } catch (error) {
      setStatus('error');
      Sentry.addBreadcrumb({ message: 'Unable to upload bank receipt' });
      Sentry.captureException(error);
    }
  };

  const formattedCapital = formatters.formatNumber(capital);
  const isUploading = status === 'uploaded' || status === 'uploading';
  return (
    <StepContainer>
      <PageHeading>Skaff en bedriftskonto</PageHeading>

      <p>
        Nå skal du opprette en bankkonto i en valgfri bank. Når du har mottatt
        papirene fra banken kan du fullføre stiftelsen av {companyName}.
      </p>

      <p>
        <strong>Slik går du fram</strong>
      </p>

      <ol>
        <li>
          <a href={documentUrl || undefined} download="Stiftelsesdokument.pdf">
            Last ned stiftelses&shy;dokumentet
          </a>{' '}
          slik at du har det når banken spør.
        </li>
        <li>
          <strong>Velg en bank</strong> og gå gjennom prosessen for å registrere
          det nye selskapet ditt som bedriftskunde. NB: Det må være en bank som
          kan åpne konto for «selskap under stiftelse».
        </li>
        <li>
          <strong>Betal inn {formattedCapital}</strong> på kontoen banken gir
          deg.
        </li>
        <li>
          <strong>Få en bekreftelse fra banken din </strong> på at pengene er på
          kontoen. Det er vanligvis en PDF-fil. Det kan hende banken tar et lite
          gebyr.
        </li>

        <li>
          <strong>Last opp filen</strong> fra banken her.
        </li>
      </ol>
      <p>
        Når vi har fått filen fra banken din, sender vi det videre til Altinn
        for deg.
      </p>

      <form
        action={uploadReceiptURL}
        method="POST"
        encType="multipart/form-data"
        onSubmit={upload}
        css={css`
          position: relative;
        `}
        id="bankkontobekreftelse"
      >
        <FileUploadArea
          name="file"
          accept="application/pdf"
          onChange={({ target }) => {
            const file = target.files?.item(0);

            setStatus('default');
            setFileChosen(file != null);
            if (file) {
              setFileChosenIsPdf(file.type === 'application/pdf');
            }
          }}
          disabled={isUploading}
          error={fileChosen && !fileChosenIsPdf}
          css={css`
            min-height: 150px;
            text-align: center;
            border-color: var(--system-blue-700);
            background: var(--blue-200);
          `}
        >
          {files => {
            const file = files[0];
            if (file !== undefined && file.type !== 'application/pdf') {
              return 'Filen må være en PDF';
            }

            return (
              <>
                {file
                  ? file.name
                  : 'Trykk her eller dra bekreftelsen fra banken hit'}
                {isUploading && (
                  <UploadingSpinnerWrapper>
                    <ProgressSpinner size={48} />
                  </UploadingSpinnerWrapper>
                )}
              </>
            );
          }}
        </FileUploadArea>
        <input type="hidden" name="id" value={id} />
        <p
          css={css`
            text-align: center;
          `}
        >
          <Button type="submit" disabled={isUploading || !fileChosen}>
            Last opp
          </Button>
        </p>
      </form>
      {status === 'error' && (
        <p>
          Noe gikk galt under opplasting. Prøv på nytt eller send PDF-filen til{' '}
          <a
            href={`${folioEmailAddressUrl}?subject=${encodeURIComponent(
              `Bekreftelse på innbetalt aksjekapital for ${companyName}`,
            )}`}
          >
            {folioEmailAddress}
          </a>
          .
        </p>
      )}
      {status === 'wrong-file' && (
        <p
          css={css`
            color: ${colors.red};
          `}
        >
          Oi, dette ble ikke helt riktig! Filen du lastet opp er
          stiftelses&shy;dokumentet – det vi trenger er en bekreftelse på at du
          har betalt inn start&shy;kapitalen. Et slikt dokument får du hos
          banken etter at du har opprettet bedrifts&shy;konto og betalt inn
          pengene.
        </p>
      )}

      {bankSelection === 'OTHER_NAGGED' ? (
        <div
          css={css`
            ${space([32, 32, 40], 'margin-vertical')};
          `}
        >
          Skal vi heller gjøre alt dette for deg?{' '}
          <a href={onboardingUrl}>Åpne Folio-konto for selskapet nå</a>.
        </div>
      ) : null}
    </StepContainer>
  );
};

export const OtherBank: React.FC = () => {
  const { documentUrl, id, bankSelection } = useSelector(selector);
  const { foundingActions } = useActions();
  const onSetBankSelection = async (selection: MaBankSelection) => {
    await setBankSelection({ id, selection });
    foundingActions.updateFounding({
      bankSelection: selection,
    });

    // This will show another screen, but not change the URL
    // so we have to scroll manually.
    window.scrollTo(0, 0);
  };

  return (
    <>
      <PageHeading>Stifte AS med en annen banktjeneste</PageHeading>

      <p>
        Bruk stiftelses&shy;dokumentet til å opprette konto i en bank etter eget
        ønske. Etterpå registrerer du selskapet i
        Brønnøy&shy;sund&shy;registrene selv, eller med hjelp fra banken din.
      </p>

      <p>
        <strong>Slik går du fram</strong>
      </p>

      <ol
        css={css`
          padding-left: 24px;

          li:not(:first-of-type) {
            ${space([8], 'margin-top')}
          }
        `}
      >
        <li>
          <a href={documentUrl || undefined} download="Stiftelsesdokument.pdf">
            Last ned stiftelses&shy;dokumentet
          </a>{' '}
          slik at du har det når banken spør. Vi sender det til deg på e-post
          også, for sikkerhets skyld.
        </li>
        <li>
          <b>Finn en bank</b> som tilbyr aksje&shy;kapital&shy;konto til
          selskaper under stiftelse.
        </li>
        <li>
          <b>Sett inn aksjekapitalen</b> på konto.
        </li>
        <li>
          <b>Få en bekreftelse fra banken</b> om at pengene er på konto. Det er
          vanligvis en PDF-fil. Det hender de tar et lite gebyr.
        </li>

        <li>
          <b>Send inn samordnet register&shy;melding</b>. Du fyller inn{' '}
          <a href="https://info.altinn.no/skjemaoversikt/bronnoysundregistrene/registrere-nye-og-endre-eksisterende-foretak-og-enheter---samordnet-registermelding/">
            skjemaet i Altinn
          </a>{' '}
          for å registrere AS. Her legger du ved stiftelses&shy;dokumentet og
          bekreftelsen fra banken din.
        </li>

        <li>
          <b>Vent på at selskapet registreres</b>. Du må regne med å vente 2–4
          uker.
        </li>
      </ol>

      {bankSelection === 'OTHER_NAGGED' ? (
        <div
          css={css`
            background: ${colors.blueLight};
            ${space([16], 'padding')};
            border-radius: var(--border-radius);
          `}
        >
          Skal vi heller gjøre alt dette for deg?{' '}
          <a
            href={window.location.href}
            onClick={async event => {
              event.preventDefault();
              await onSetBankSelection('UNSET');
            }}
          >
            Åpne Folio-konto for selskapet nå
          </a>
          .
        </div>
      ) : null}
    </>
  );
};
